import { InnerBox } from "@/components/elements/InnerBox";
import { Stack, Typography, Box, styled } from "@mui/material";
import { FC, useCallback, useState, useMemo, FocusEvent, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "@/routes/config";
import { Button } from "@/components/elements/Button";
import { IconBox } from "@/components/elements/IconBox";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useCountCaseMatchesSummary, useCountSummary, useMonthlyTarget } from "@/api/services/main/dashboard";
import { MonthlyTargetModal } from "@/features/dashboard/components/MonthlyTargetModal";
import { useVisible } from "@/hooks/useVisible";
import { DashboardMonthlyStatistics } from "@/features/dashboard/components/DashboardMonthlyStatistics";
import { IconStatistics, IconChevronDown, IconPlaylistAddCheck, IconFileExport } from "@/assets/icons";
import { convertMonthlyTargetToFormDataRes } from "@/features/dashboard/utils/convertMonthlyTargetFormData";
import { CSV_EXPORT_FAILED_MESSAGE } from "@/constants/common";
import { exportMonthlyStatisticsCsv } from "@/features/dashboard/utils/export-monthly-statistics-csv";
import { black } from "@/theme/colors";
import { DashboardMonthlyCaseMatchesStatistics } from "@/features/dashboard/components/DashboardMonthlyCaseMatchesStatistics";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { YEAR_MONTH_STR_FORMAT, YEAR_MONTH_FORMAT } from "@/constants/datetime";
import { toast } from "@/utils/toast";

// "今月" when targetMonth equals current month; otherwise "{M}月" (no leading zero).
const formatMonthLabel = (month: Dayjs): string => (month.isSame(dayjs(), "month") ? "今月" : `${month.format("M")}月`);

export const DashboardStatistics: FC = () => {
  const navigate = useNavigate();
  const [targetMonth, setTargetMonth] = useState<Dayjs>(dayjs());
  const [apiMonth, setApiMonth] = useState<Dayjs>(dayjs());
  const [pickerKey, setPickerKey] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const latestSelectedMonthRef = useRef<Dayjs>(dayjs());

  const monthlyTargetModal = useVisible();
  const monthLabel = useMemo(() => {
    return formatMonthLabel(apiMonth);
  }, [apiMonth]);
  // Call API when date changes
  const { data: countSummary, isLoading: isLoadingCountSummary } = useCountSummary({
    targetMonth: apiMonth.format("YYYY-MM"),
  });

  const { data: countCaseMatchesSummary, isLoading: isLoadingCaseMatchesCountSummary } = useCountCaseMatchesSummary({
    targetMonth: apiMonth.format("YYYY-MM"),
  });

  // count-summary only returns currentMonth/previousMonth, so the 先月 row's 前月比 on the
  // 新規登録人材数 card needs 先々月 — take it from this call's previousMonth.
  const { data: prevCountSummary } = useCountSummary({
    targetMonth: apiMonth.subtract(1, "month").format("YYYY-MM"),
  });

  // Fetch monthly target when modal is visible
  const { data: monthlyTargetData, isLoading: isLoadingMonthlyTarget } = useMonthlyTarget({ targetMonth: apiMonth.format("YYYY-MM") });
  const { data: prevMonthlyTargetData } = useMonthlyTarget({ targetMonth: apiMonth.subtract(1, "month").format("YYYY-MM") });

  // Handle date change
  const handleDateChange = (newDate: Dayjs | null) => {
    if (newDate) {
      if (newDate && newDate.isValid()) {
        setTargetMonth(newDate);
        latestSelectedMonthRef.current = newDate;
      }
    }
  };
  const handleBlur = (e?: FocusEvent<HTMLInputElement | HTMLTextAreaElement>, val?: Dayjs | null) => {
    if (e?.target) {
      const textVal = e.target.value || "";
      const isTextValid = dayjs(textVal, YEAR_MONTH_FORMAT, true).isValid();
      if (!isTextValid) {
        setPickerKey((prev) => prev + 1);
        setTargetMonth(apiMonth);
        return;
      }
    }
    const monthToUse = val || latestSelectedMonthRef.current;
    if (!monthToUse || !monthToUse.isValid()) return;
    if (monthToUse.isSame(apiMonth)) return;
    setApiMonth(monthToUse);
  };

  const handleChevronClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const isOpen = !!document.querySelector(".MuiPickersPopper-root");
    if (isOpen) {
      document.body.dispatchEvent(new PointerEvent("pointerdown", { bubbles: true }));
      return;
    }
    if (containerRef.current) {
      const inputBase = containerRef.current.querySelector(".MuiInputBase-root");
      if (inputBase) {
        inputBase.dispatchEvent(new MouseEvent("click", { bubbles: true }));
      }
    }
  }, []);

  const handleContainerKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
      e.target.blur();
    }
  }, []);

  // Lazy export — fetches all 4 performance-detail datasets on click (no extra requests on render)
  const [isExporting, setIsExporting] = useState(false);
  const handleExportMonthlyCsv = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await exportMonthlyStatisticsCsv({ targetMonth: apiMonth });
    } catch {
      toast.error(CSV_EXPORT_FAILED_MESSAGE);
    } finally {
      setIsExporting(false);
    }
  }, [apiMonth, isExporting]);

  return (
    <Stack>
      <InnerBox
        title={`${monthLabel}の実績値と目標値`}
        variantTitle="head20Bold"
        bordered={true}
        showDivider={false}
        rightContent={
          <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={3}>
            <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={1}>
              <Typography>対象年月</Typography>
              <DatePickerWithChevron ref={containerRef} onKeyDown={handleContainerKeyDown}>
                <InputDatePicker
                  key={pickerKey}
                  views={["year", "month"]}
                  openTo="month"
                  format={YEAR_MONTH_FORMAT}
                  value={targetMonth}
                  onChange={handleDateChange}
                  onBlur={handleBlur}
                  minDate={dayjs("1900/01/01")}
                  maxDate={dayjs()}
                  enableCloseIcon={false}
                  autoClamp
                  sx={{
                    maxWidth: "128px",
                  }}
                />
                <ChevronIcon onClick={handleChevronClick}>
                  <IconChevronDown color={black[80]} />
                </ChevronIcon>
              </DatePickerWithChevron>
            </Stack>
            <Button size="sm" variant="outline" onClick={monthlyTargetModal.open} disabled={isLoadingMonthlyTarget}>
              <IconBox sx={{ marginRight: "2px" }}>
                <IconStatistics />
              </IconBox>
              目標値設定
            </Button>
          </Stack>
        }
      >
        <DashboardMonthlyStatistics
          countSummaryData={countSummary?.data}
          isLoadingCountSummary={isLoadingCountSummary}
          monthlyTargetData={monthlyTargetData?.data}
          isLoadingMonthlyTarget={isLoadingMonthlyTarget}
        />
        <Stack mt={3}>
          <Typography variant="head20Bold">マッチング実績値と目標値</Typography>
        </Stack>
        {isLoadingCaseMatchesCountSummary ? (
          <LoadingArea />
        ) : (
          <>
            <DashboardMonthlyCaseMatchesStatistics
              title={`今月 (${apiMonth.format(YEAR_MONTH_STR_FORMAT)})`}
              targetMonth={apiMonth}
              currentCaseMatchesSummaryData={countCaseMatchesSummary?.data?.currentMonth}
              prevCaseMatchesSummaryData={countCaseMatchesSummary?.data?.previousMonth}
              monthlyTargetData={monthlyTargetData?.data}
              currentNewTalents={countSummary?.data?.currentMonth.talents}
              prevNewTalents={countSummary?.data?.previousMonth.talents}
            />
            <DashboardMonthlyCaseMatchesStatistics
              title={`先月 (${apiMonth.subtract(1, "month").format(YEAR_MONTH_STR_FORMAT)})`}
              targetMonth={apiMonth.subtract(1, "month")}
              currentCaseMatchesSummaryData={countCaseMatchesSummary?.data?.previousMonth}
              prevCaseMatchesSummaryData={countCaseMatchesSummary?.data?.twoMonthsAgo}
              monthlyTargetData={prevMonthlyTargetData?.data}
              currentNewTalents={countSummary?.data?.previousMonth.talents}
              prevNewTalents={prevCountSummary?.data?.previousMonth.talents}
            />
          </>
        )}
        <Stack direction="row" justifyContent="flex-end" mt={3} gap={2}>
          <Button
            size="sm"
            variant="outline"
            startIcon={<IconFileExport />}
            sx={{ width: "207px" }}
            disabled={isExporting}
            onClick={handleExportMonthlyCsv}
          >
            月間実績値のCSV出力
          </Button>
          <Button
            size="sm"
            variant="outline"
            startIcon={<IconPlaylistAddCheck />}
            sx={{
              width: "172px",
            }}
            onClick={() => navigate(AppRoutes.performanceDetail)}
          >
            年間実績表を見る
          </Button>
        </Stack>
      </InnerBox>
      <MonthlyTargetModal
        modal={monthlyTargetModal}
        targetMonth={apiMonth.format("YYYY-MM")}
        defaultValues={convertMonthlyTargetToFormDataRes(monthlyTargetData?.data)}
      />
    </Stack>
  );
};

const DatePickerWithChevron = styled(Box)`
  position: relative;
  display: inline-block;
  .MuiInputBase-root {
    padding-left: 32px !important;
  }
`;

const ChevronIcon = styled(IconBox)`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  z-index: 3;
  cursor: pointer;
`;
